export type InvoiceJournalTimelineEntry = {
  action?: string | null;
  timestamp?: string | null;
  recordedAt?: string | null;
  effectiveAt?: string | null;
};

export type InvoiceJournalPaymentEntryLike = {
  _id?: string | null;
  type?: string | null;
  amount?: number | null;
  method?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  receivedDate?: string | null;
  effectiveAt?: string | null;
  recordedAt?: string | null;
  reference?: string | null;
  chequeNumber?: string | null;
};

const titleCaseWords = (value?: string | null) =>
  (value ?? "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(word =>
      word === "eft" ? "EFT" : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");

/** Labels journal field keys like `recipientContact` → `Recipient Contact`. */
export const formatJournalFieldLabel = (field?: string | null) => {
  if (!field) return "";
  return field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(word => {
      const lower = word.toLowerCase();
      if (lower === "eft") return "EFT";
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

const formatAddressValue = (address: Record<string, unknown>) =>
  [
    address.line1,
    address.line2,
    address.city,
    address.subdivision,
    address.postalCode,
    address.country,
  ]
    .map(part => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(", ");

const formatRecipientContactValue = (contact: Record<string, unknown>) => {
  const name = [contact.firstName, contact.lastName]
    .map(part => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(" ");
  const parts = [
    name || null,
    typeof contact.email === "string" ? contact.email.trim() : null,
    typeof contact.phone === "string" ? contact.phone.trim() : null,
    contact.address && typeof contact.address === "object"
      ? formatAddressValue(contact.address as Record<string, unknown>)
      : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
};

/**
 * Renders Mixed `before`/`after` journal values. Objects must not go through
 * `String(...)` (that produces `[object Object]`).
 */
export const formatJournalChangeValue = (value: unknown): string => {
  if (value == null || value === "") return "—";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value.map(formatJournalChangeValue).filter(part => part !== "—");
    return parts.length ? parts.join(", ") : "—";
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (
      "email" in obj ||
      "firstName" in obj ||
      "lastName" in obj ||
      "phone" in obj ||
      "address" in obj
    ) {
      return formatRecipientContactValue(obj);
    }
    if (
      "line1" in obj ||
      "line2" in obj ||
      "city" in obj ||
      "postalCode" in obj
    ) {
      return formatAddressValue(obj) || "—";
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }
  return String(value);
};

/**
 * Card payments on this platform are always processed through Stripe, so
 * "Card" is relabelled as "Stripe" for clarity, with the card brand/last 4
 * (when known) shown as supporting detail.
 */
export const describeInvoiceJournalPayment = (
  entry: InvoiceJournalPaymentEntryLike,
) => {
  const isCard = entry.method === "CARD";
  const methodLabel = isCard ? "Stripe" : titleCaseWords(entry.method);
  const detailParts = [
    isCard ? titleCaseWords(entry.cardBrand) || "Card" : null,
    entry.cardLast4 ? `•••• ${entry.cardLast4}` : null,
    !isCard && entry.reference ? entry.reference : null,
    !isCard && entry.chequeNumber ? `Cheque ${entry.chequeNumber}` : null,
  ].filter(Boolean);
  const isComplimentaryMethod = entry.method === "COMPLIMENTARY";
  const isComp = isComplimentaryMethod && (entry.amount ?? 0) > 0;
  return {
    title: isComp
      ? "Invoice comped · Complimentary"
      : isComplimentaryMethod
        ? "Settled · No payment required"
        : `Payment recorded${methodLabel ? ` · ${methodLabel}` : ""}`,
    detail: detailParts.length ? detailParts.join(" ") : undefined,
  };
};

export type JournalHistoryEntryLike = InvoiceJournalTimelineEntry & {
  eventId?: string | null;
  action?: string | null;
  note?: string | null;
  references?: {
    paymentEntryId?: string | null;
    orderId?: string | null;
  } | null;
};

export const timestampValue = (timestamp?: string | null) => {
  if (!timestamp) return Number.NEGATIVE_INFINITY;
  const value = Date.parse(timestamp);
  return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
};

/**
 * Record-payment-that-settles used to write both `payment_recorded` and `paid`.
 * Collapse those into a single paid row (keeping the paymentEntryId link) so
 * the journal shows one admin action.
 */
export const consolidateRecordPaymentHistory = <
  T extends JournalHistoryEntryLike,
>(
  entries: readonly T[],
): T[] => {
  const suppressEventIds = new Set<string>();
  const paymentEntryIdForPaidEventId = new Map<string, string>();

  for (const paid of entries) {
    if (String(paid.action ?? "").toLowerCase() !== "paid") continue;

    if (paid.references?.paymentEntryId) {
      for (const candidate of entries) {
        if (
          String(candidate.action ?? "").toLowerCase() === "payment_recorded" &&
          String(candidate.references?.paymentEntryId ?? "") ===
            String(paid.references.paymentEntryId) &&
          candidate.eventId
        ) {
          suppressEventIds.add(String(candidate.eventId));
        }
      }
      continue;
    }

    const paidTime = timestampValue(paid.recordedAt ?? paid.timestamp);
    const match = entries.find(candidate => {
      if (String(candidate.action ?? "").toLowerCase() !== "payment_recorded") {
        return false;
      }
      const paymentEntryId = candidate.references?.paymentEntryId;
      if (!paymentEntryId) return false;
      const candidateTime = timestampValue(
        candidate.recordedAt ?? candidate.timestamp,
      );
      return Math.abs(paidTime - candidateTime) <= 60_000;
    });
    if (!match?.eventId || !match.references?.paymentEntryId) continue;
    suppressEventIds.add(String(match.eventId));
    if (paid.eventId) {
      paymentEntryIdForPaidEventId.set(
        String(paid.eventId),
        String(match.references.paymentEntryId),
      );
    }
  }

  return entries
    .filter(
      entry => !entry.eventId || !suppressEventIds.has(String(entry.eventId)),
    )
    .map(entry => {
      if (!entry.eventId) return entry;
      const paymentEntryId = paymentEntryIdForPaidEventId.get(
        String(entry.eventId),
      );
      if (!paymentEntryId) return entry;
      return {
        ...entry,
        references: {
          ...(entry.references ?? {}),
          paymentEntryId,
        },
      };
    });
};

/**
 * Synthesizes "Payment" rows for the chronological history from payment
 * entries that aren't already represented by a changeHistory event (for
 * example, older invoices paid before an event was recorded for every
 * payment). Payment entries already referenced by a changeHistory event are
 * skipped to avoid showing the same payment twice.
 */
export const buildSyntheticPaymentHistoryEntries = (
  paymentEntries: readonly InvoiceJournalPaymentEntryLike[] | null | undefined,
  changeHistory:
    | readonly { references?: { paymentEntryId?: string | null } | null }[]
    | null
    | undefined,
) => {
  const referencedIds = new Set(
    (changeHistory ?? [])
      .map(entry => entry.references?.paymentEntryId)
      .filter(Boolean)
      .map(String),
  );
  return (paymentEntries ?? [])
    .filter(
      entry =>
        entry.type === "PAYMENT" &&
        entry._id &&
        !referencedIds.has(String(entry._id)),
    )
    .map(entry => ({
      eventId: `payment-entry:${entry._id}`,
      action: "PAYMENT_RECORDED",
      // Prefer the recorded instant so date-only received dates don't sort as
      // midnight ahead of/behind the real journal sequence.
      timestamp:
        entry.recordedAt ?? entry.effectiveAt ?? entry.receivedDate,
      recordedAt: entry.recordedAt ?? undefined,
      effectiveAt: entry.effectiveAt ?? entry.receivedDate ?? undefined,
      performedByName: undefined as string | undefined,
      paymentEntry: entry,
    }));
};

const ACCOUNTING_METADATA_UPDATED = "accounting_metadata_updated";

/** True for business dates like `2026-07-17` (no clock time). */
export const isDateOnlyTimestamp = (value?: string | null) =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

/**
 * Prefer a full instant for ordering/display. Date-only `effectiveAt` values
 * (received/accounting dates) would otherwise sort/display as midnight and
 * bury the real journal sequence under later "Paid" events.
 */
export const getInvoiceJournalTimelineTimestamp = (
  entry: InvoiceJournalTimelineEntry,
) => {
  if (
    entry.action === ACCOUNTING_METADATA_UPDATED ||
    isDateOnlyTimestamp(entry.effectiveAt)
  ) {
    return (
      entry.recordedAt ?? entry.timestamp ?? entry.effectiveAt ?? undefined
    );
  }
  return entry.effectiveAt ?? entry.recordedAt ?? entry.timestamp ?? undefined;
};

export const getPaymentEntrySortTimestamp = (
  entry: InvoiceJournalPaymentEntryLike & {
    createdDateISO?: string | Date | null;
  },
) => {
  const created =
    entry.createdDateISO instanceof Date
      ? entry.createdDateISO.toISOString()
      : entry.createdDateISO;
  return (
    entry.recordedAt ??
    created ??
    (isDateOnlyTimestamp(entry.effectiveAt) ? undefined : entry.effectiveAt) ??
    entry.receivedDate ??
    undefined
  );
};

/** Newest payment / void / refund first. */
export const sortPaymentEntriesNewestFirst = <
  T extends InvoiceJournalPaymentEntryLike & {
    createdDateISO?: string | Date | null;
  },
>(
  entries: readonly T[],
) =>
  [...entries].sort(
    (a, b) =>
      timestampValue(getPaymentEntrySortTimestamp(b)) -
      timestampValue(getPaymentEntrySortTimestamp(a)),
  );

export const sortInvoiceJournalEntries = <
  T extends InvoiceJournalTimelineEntry,
>(
  entries: readonly T[],
) =>
  [...entries].sort((a, b) => {
    const timelineDifference =
      timestampValue(getInvoiceJournalTimelineTimestamp(b)) -
      timestampValue(getInvoiceJournalTimelineTimestamp(a));
    if (timelineDifference) return timelineDifference;

    return (
      timestampValue(b.recordedAt ?? b.timestamp) -
      timestampValue(a.recordedAt ?? a.timestamp)
    );
  });
